/* eslint-disable no-comments/disallowComments */
import React, { useState } from 'react';
import { Collapse, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Colors } from 'library';
import { BaseButton } from './BaseButton';
import { CardTone, TONE_BG, TONE_BORDER } from './Card';

type SectionPadding = 'sm' | 'md';

const PADDING_PX: Record<SectionPadding, number> = { sm: 16, md: 24 };

interface SplitCardProps {
    children: React.ReactNode;
    /** Border tone for the whole card: `default` (grey) or any `CardTone` (e.g. `success` for a green outline). */
    tone?: CardTone;
    className?: string;
}

/**
 * One bordered card split into stacked sections separated by dividers.
 * Compose it from `SplitCardSection` and `SplitCardExpandableSection`.
 */
export function SplitCard({ children, tone = 'default', className }: SplitCardProps) {
    return (
        <Container $tone={tone} className={className}>
            {children}
        </Container>
    );
}

interface SplitCardSectionProps {
    children: React.ReactNode;
    /** Background tone for this section only. */
    tone?: CardTone;
    padding?: SectionPadding;
}

export function SplitCardSection({ children, tone = 'default', padding = 'sm' }: SplitCardSectionProps) {
    return (
        <Section $tone={tone} $padding={padding}>
            {children}
        </Section>
    );
}

interface SplitCardExpandableSectionProps {
    header: React.ReactNode;
    children: React.ReactNode;
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    tone?: CardTone;
    padding?: SectionPadding;
}

/** A section whose header toggles its body open, like `ExpandableCard` but inside a `SplitCard`. */
export function SplitCardExpandableSection({
    header,
    children,
    defaultExpanded = false,
    expanded: controlledExpanded,
    onExpandedChange,
    tone = 'default',
    padding = 'sm',
}: SplitCardExpandableSectionProps) {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : internalExpanded;

    const toggle = () => {
        const next = !expanded;
        if (!isControlled) {
            setInternalExpanded(next);
        }
        onExpandedChange?.(next);
    };

    return (
        <Section $tone={tone} $padding={padding}>
            <HeaderButton type="button" onClick={toggle}>
                <HeaderContent>{header}</HeaderContent>
                <Chevron $expanded={expanded}>
                    <ExpandMore sx={{ fontSize: 20, color: Colors.textSecondary }} />
                </Chevron>
            </HeaderButton>
            <Collapse in={expanded} timeout={250} unmountOnExit>
                <Body>{children}</Body>
            </Collapse>
        </Section>
    );
}

const Container = styled('div', { shouldForwardProp: (prop) => prop !== '$tone' })<{ $tone: CardTone }>(
    ({ $tone }) => ({
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${TONE_BORDER[$tone]}`,
        borderRadius: 12,
        backgroundColor: Colors.white,
        overflow: 'hidden',
        '& > * + *': {
            borderTop: `1px solid ${Colors.border}`,
        },
    }),
);

const Section = styled('div', { shouldForwardProp: (prop) => prop !== '$tone' && prop !== '$padding' })<{
    $tone: CardTone;
    $padding: SectionPadding;
}>(({ $tone, $padding }) => ({
    backgroundColor: TONE_BG[$tone],
    padding: PADDING_PX[$padding],
    boxSizing: 'border-box',
}));

const HeaderButton = styled(BaseButton)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    textAlign: 'left',
});

const HeaderContent = styled('div')({
    flex: 1,
    minWidth: 0,
});

const Chevron = styled('span', { shouldForwardProp: (prop) => prop !== '$expanded' })<{ $expanded: boolean }>(
    ({ $expanded }) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 200ms ease',
    }),
);

const Body = styled('div')({
    paddingTop: 16,
});
/* eslint-enable no-comments/disallowComments */
